import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck } from "lucide-react";
import { STATE_TONE, NEEDS_ATTENTION, stateOf, homeTileLabel, homeTileDetail } from "@/lib/billingStatus";

/* Compact External Services health strip (2026-09 Billing & Subscriptions
   monitor; stabilization round: driven by the separated status model — a
   provider whose spend retrieval WORKS but has no budget shows
   "Unknown - no budget set · USD 37.07 spent", never a bare "Unknown").
   Admin-only (the caller gates rendering on hasCap("admin.config") so this
   never appears for an ordinary employee with no need to manage billing).
   Read-only summary; the whole card links to the full page. */

export const ExternalServicesWidget = () => {
  const [providers, setProviders] = useState(null);

  useEffect(() => {
    api.get("/admin/billing/summary").then((r) => setProviders(r.data || [])).catch(() => setProviders([]));
  }, []);

  if (!providers || providers.length === 0) return null;
  // unknown_* (no budget / no data) is NOT an incident: only warning, critical,
  // integration errors and stale manual data need a human.
  const attention = providers.filter((p) => NEEDS_ATTENTION.includes(stateOf(p))).length;

  return (
    <Link to="/admin?tab=billing" data-testid="external-services-widget">
      <Card className="hover:border-primary/40 transition-colors">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-1.5 mb-2">
            <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-heading font-semibold">External Services</span>
            {attention > 0 && (
              <Badge variant="outline" className="border-0 text-[9px] bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] ml-1">
                {attention} need{attention === 1 ? "s" : ""} attention
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {providers.map((p) => (
              <div key={p.provider} className="flex items-center gap-1.5 text-xs" data-testid={`ext-service-${p.provider}`}>
                <span className="text-muted-foreground">{p.display_name}</span>
                <Badge variant="outline" title={p.state_explanation || undefined}
                  className={`border-0 text-[10px] ${STATE_TONE[stateOf(p)] || STATE_TONE.unknown_no_data}`}
                  data-testid={`ext-service-state-${p.provider}`}>
                  {homeTileLabel(p)}
                </Badge>
                {homeTileDetail(p) && (
                  <span className="text-[10px] text-muted-foreground" data-testid={`ext-service-detail-${p.provider}`}>{homeTileDetail(p)}</span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
